<script>
import { mapActions, mapState } from 'vuex';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';
import {
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  BgColorsOutlined,
} from '@ant-design/icons-vue';
import CompanyModal from '@/components/modals/Company-modal.vue';
import EditColorsModal from '@/components/pageContent/EditColorsModal.vue';
import dayjs from 'dayjs';
import pageUrls from '@/router/pageUrls';
import { mapColorsToFullObject } from '@/utils/colorUtils';
import columns from './columns';

const { companiesPage } = pageUrls;
const b = bem('companies-page');

export default {
  components: {
    PageHeader,
    EditOutlined,
    DeleteOutlined,
    ApiOutlined,
    CompanyModal,
    EditColorsModal,
    BgColorsOutlined,
  },
  data() {
    return {
      title: 'PODs',
      columns: columns(),
      limit: 10,
      offset: 0,
      showModal: false,
      selectedCompany: null,
      isEditColorsModalVisible: false,
      selectedCompanyColors: {
        color1: '',
        color2: '',
        color3: '',
      },
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getCompaniesAction: 'companies/getCompanies',
      deleteCompanyAction: 'companies/deleteCompany',
      getCompanyColors: 'companies/getCompanyColors',
      saveCompanyColors: 'companies/saveCompanyColors',
    }),
    getCompanies() {
      this.getCompaniesAction({ limit: this.limit, offset: this.offset });
    },
    setPagination(page, pageSize) {
      this.offset = (page - 1) * pageSize;
      this.limit = pageSize;
    },
    openCreateModal() {
      this.selectedCompany = null;
      this.showModal = true;
    },
    openEditModal(company) {
      this.selectedCompany = company;
      this.showModal = true;
    },
    deleteCompany(id) {
      this.deleteCompanyAction({ id, limit: this.limit, offset: this.offset });
    },
    formatDate(date) {
      return date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-';
    },
    navigateToApiConfig(companyId) {
      this.$router.push(`${companiesPage}/${companyId}/api-config`);
    },
    navigateToCompany(companyId) {
      this.$router.push(`${companiesPage}/${companyId}`);
    },
    async handleEditColors(company) {
      this.selectedCompany = company;
      this.selectedCompanyColors = {
        color1: '',
        color2: '',
        color3: '',
      };
      try {
        const colors = await this.getCompanyColors(company.id);
        if (colors && colors.length > 0) {
          const colorMap = mapColorsToFullObject(colors);
          this.selectedCompanyColors = {
            color1: colorMap.color1?.hex || '',
            color2: colorMap.color2?.hex || '',
            color3: colorMap.color3?.hex || '',
            ids: {
              color1: colorMap.color1?.id,
              color2: colorMap.color2?.id,
              color3: colorMap.color3?.id,
            },
          };
        }
      } catch {
        // Colors not found, using defaults
      }
      this.isEditColorsModalVisible = true;
    },
    handleCloseEditColorsModal() {
      this.isEditColorsModalVisible = false;
    },
    async handleSaveColors(colorsData) {
      try {
        await this.saveCompanyColors({
          companyId: this.selectedCompany.id,
          colors: colorsData,
          existingIds: this.selectedCompanyColors.ids,
        });
        this.isEditColorsModalVisible = false;
      } catch {
        // Error handled by store action
      }
    },
  },
  mounted() {
    this.getCompanies();
  },
  computed: {
    ...mapState({
      companies: (state) => state.companies.companies,
      companiesTotal: (state) => state.companies.companiesTotal,
      isCompaniesLoaded: (state) => state.companies.isCompaniesLoaded,
    }),
  },
  watch: {
    offset() {
      this.getCompanies();
    },
    limit() {
      this.getCompanies();
    },
  },
};
</script>

<template>
  <PageHeader :title="title" />
  <div :class="b()">
    <div :class="b('top-wrapper')">
      <a-button type="primary" html-type="button" @click="openCreateModal">
        Create New Company
      </a-button>
    </div>

    <CompanyModal
      v-if="showModal"
      v-model:show="showModal"
      :company="selectedCompany"
      :limit="limit"
      :offset="offset"
    />

    <EditColorsModal
      :visible="isEditColorsModalVisible"
      :initial-colors="selectedCompanyColors"
      @close="handleCloseEditColorsModal"
      @save="handleSaveColors"
    />

    <div :class="b('table')">
      <a-table
        :columns="columns"
        :data-source="companies"
        :loading="!isCompaniesLoaded"
        :pagination="{ pageSize: limit, total: companiesTotal, onChange: setPagination }"
        :row-key="(record) => record.id"
        :custom-row="(record) => ({
          onClick: (event) => {
            const isActionButton = event.target.closest('button, .ant-btn');
            if (!isActionButton) {
              navigateToCompany(record.id);
            }
          },
          style: { cursor: 'pointer' }
        })"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'addedAt'">
            {{ formatDate(record.addedAt) }}
          </template>
          <template v-else-if="column.dataIndex === 'actions'">
            <div :class="b('actions')">
              <a-button
                type="default"
                html-type="button"
                @click="navigateToApiConfig(record.id)"
                title="API Configurations"
              >
                <ApiOutlined /> API Config
              </a-button>
              <a-button
                type="default"
                html-type="button"
                @click="handleEditColors(record)"
                title="Company colors"
              >
                <BgColorsOutlined /> Company colors
              </a-button>
              <a-button
                type="primary"
                html-type="button"
                @click="openEditModal(record)"
              >
                <EditOutlined /> Edit
              </a-button>
              <a-popconfirm
                title="Are you sure you want to delete this company?"
                @confirm="deleteCompany(record.id)"
              >
                <a-button type="primary" html-type="button" danger>
                  <DeleteOutlined /> Delete
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<style scoped lang="scss">
.companies-page {
  margin: 0 20px;

  &__top-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: 10px 20px;
  }

  &__table {
    padding: 10px 20px;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }
}
</style>
